import { createRoot } from "react-dom/client";
import type { Prescription, PrescriptionLine } from "@/core/clinical/types";
import { formatPrescriptionPosology } from "@/core/clinical/types";
import { patientFullName } from "@/core/patients/types";
import type { Patient } from "@/core/patients/types";

interface PrescriptionRemainderPrintSheetProps {
  prescription: Prescription;
  patient: Patient;
  lines: PrescriptionLine[];
}

export function PrescriptionRemainderPrintSheet({
  prescription,
  patient,
  lines,
}: PrescriptionRemainderPrintSheetProps) {
  const dateLabel = new Date().toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="ph-invoice-print-sheet">
      <div className="ph-invoice-header">
        <div>
          <div className="ph-invoice-brand">ITaaS Hospitol</div>
          <div className="ph-invoice-sub">Ordonnance restante — achat externe</div>
        </div>
        <div className="ph-invoice-meta text-end">
          <div className="ph-invoice-title">À DÉLIVRER AILLEURS</div>
          <div className="small text-muted">{dateLabel}</div>
        </div>
      </div>

      <div className="mb-4">
        <div className="ph-invoice-section-title">Patient</div>
        <div className="fw-semibold">{patientFullName(patient)}</div>
        <div className="small text-muted">{patient.fileNumber}</div>
        {patient.phone && <div className="small">Tél. {patient.phone}</div>}
      </div>

      <p className="small text-muted mb-3">
        Les médicaments ci-dessous n&apos;étaient pas disponibles en stock hospitalier.
        Présentez ce document en officine externe.
      </p>

      <table className="table table-sm ph-invoice-table">
        <thead>
          <tr>
            <th>Médicament</th>
            <th>Posologie</th>
            <th className="text-end">Qté</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.id}>
              <td className="fw-medium">{line.productLabel}</td>
              <td className="small">{formatPrescriptionPosology(line)}</td>
              <td className="text-end">{line.quantity}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="ph-invoice-footer mt-4">
        <div className="small text-muted">
          Ordonnance {prescription.id.slice(-8)} — document informatif sans valeur de facturation.
        </div>
        <div className="mt-4 pt-3 border-top">
          <div className="small text-muted">Signature pharmacien hospitalier</div>
          <div style={{ height: "3rem" }} />
        </div>
      </div>
    </div>
  );
}

export function printPrescriptionRemainder(
  prescription: Prescription,
  patient: Patient,
  lines: PrescriptionLine[],
): void {
  const portal = document.createElement("div");
  portal.className = "ph-print-portal";
  portal.setAttribute("aria-hidden", "true");
  document.body.appendChild(portal);

  const root = createRoot(portal);
  root.render(
    <PrescriptionRemainderPrintSheet
      prescription={prescription}
      patient={patient}
      lines={lines}
    />,
  );

  window.setTimeout(() => {
    window.print();
    root.unmount();
    portal.remove();
  }, 150);
}
